/**
 * Every statement that has ever run on a device database, in order.
 *
 * Hand-written and keyed on PRAGMA user_version — drizzle-kit, bundled .sql
 * files and metro changes are deliberately out of the loop. `db.ts` steps a
 * real connection through this ladder; the Drizzle tables in db-schema.ts
 * describe the same database and MUST CHANGE TOGETHER with it.
 *
 * **A migration that has shipped is never edited.** Add the next one. A device
 * at version 5 runs only step 5, so changing step 3 changes what a NEW install
 * gets and nothing else — which is how two devices end up with two different
 * schemas both claiming the same version.
 *
 * **Why this is its own file and not the top of db.ts.** db.ts imports
 * expo-sqlite, so it cannot be loaded under bun, so a test could never run the
 * REAL migration text — it would have to copy it, and a copied backfill that
 * drifts from the shipped one is exactly the bug worth catching. Here there is
 * nothing but strings and no imports at all, so
 * `db-migrations.sqlite.test.ts` runs the same characters a phone runs.
 */

/**
 * Migration 1: the whole schema, for a database that is being born.
 * Mirrors db-schema.ts table for table, index for index — change both.
 */
const MIGRATION_1 = `
CREATE TABLE entry_mirror (
  entry_id TEXT PRIMARY KEY,
  record_id TEXT NOT NULL,
  version INTEGER NOT NULL,
  version_id TEXT,
  server_seq INTEGER,
  is_current INTEGER NOT NULL DEFAULT 1,
  deleted_at TEXT,
  day TEXT,
  signed_value REAL,
  pending INTEGER NOT NULL DEFAULT 0,
  values_json TEXT NOT NULL,
  created_by TEXT,
  source_channel TEXT,
  created_at TEXT NOT NULL,
  updated_at TEXT NOT NULL
);
CREATE INDEX entry_mirror_list ON entry_mirror(record_id, day DESC) WHERE deleted_at IS NULL;
CREATE INDEX entry_mirror_seq ON entry_mirror(server_seq);
CREATE INDEX entry_mirror_agg ON entry_mirror(record_id, day) WHERE deleted_at IS NULL;

CREATE TABLE outbox (
  op_id TEXT PRIMARY KEY,
  kind TEXT NOT NULL,
  record_id TEXT NOT NULL,
  entry_id TEXT NOT NULL,
  values_json TEXT NOT NULL,
  field_set_ver INTEGER,
  failed_reason TEXT,
  created_at TEXT NOT NULL
);

CREATE TABLE record_catalog (
  id TEXT PRIMARY KEY,
  name TEXT NOT NULL,
  kind TEXT NOT NULL,
  noun_key TEXT,
  entry_noun_key TEXT,
  currency TEXT,
  template_id TEXT,
  direction TEXT,
  entry_count INTEGER NOT NULL DEFAULT 0,
  headline_value REAL,
  last_entry_at TEXT,
  field_set_ver INTEGER NOT NULL DEFAULT 0
);

CREATE TABLE schema_cache (
  record_id TEXT PRIMARY KEY,
  field_set_ver INTEGER NOT NULL,
  schema_json TEXT NOT NULL
);

CREATE TABLE sync_meta (
  key TEXT PRIMARY KEY,
  value TEXT NOT NULL
);
`;

/**
 * Migration 2: where each record's insight cards sit, and in what order.
 *
 * One row per record holding the whole ordered list as JSON, following the
 * schema_cache precedent: add, remove and reorder are then one atomic write.
 * No row means "this record has never been customized, so derive the layout
 * from its summary view"; a row holding `[]` means "the user removed them
 * all". The difference matters, which is why absence is not an empty list.
 */
const MIGRATION_2 = `
CREATE TABLE insight_layout (
  record_id TEXT PRIMARY KEY,
  layout_json TEXT NOT NULL,
  updated_at TEXT NOT NULL
);
`;

/**
 * Migration 3: the business-wide insight layout, and the index its cards need.
 *
 * Its own table, keyed by tenant, rather than a reserved row in
 * `insight_layout`. Three reasons, and the first is the one that decided it:
 * `pruneCatalog` deletes `insight_layout` rows by record id when a record
 * leaves the device, so a business layout hiding in that table would need a
 * carve-out there and in every maintenance path written afterwards. A column
 * called `record_id` holding something that is not a record is also a lie the
 * next reader pays for. And keying by tenant follows the grain `sync_meta`
 * already set for its cursors.
 *
 * The index is not optional. `entry_mirror_agg` leads on `record_id`, and a
 * business-wide question names no record — SQLite has no skip-scan, so without
 * a day-only index every windowed business card would scan the whole mirror.
 * The partial predicate matches the WHERE these statements always carry, so it
 * qualifies. `insight-business-query.sqlite.test.ts` reads the real query plan
 * and fails if that stops being true.
 */
const MIGRATION_3 = `
CREATE TABLE business_insight_layout (
  tenant_id TEXT PRIMARY KEY,
  layout_json TEXT NOT NULL,
  updated_at TEXT NOT NULL
);
CREATE INDEX entry_mirror_day ON entry_mirror(day) WHERE deleted_at IS NULL;
`;

/**
 * The provenance pair, mirrored.
 *
 * A pulled row already carries link_id and source_entry_id, and without them
 * here the device cannot draw the Auto badge or refuse an edit while offline.
 * Two nullable columns, which is all SQLite's ALTER TABLE allows and all that
 * is wanted: a row a person typed carries neither.
 */
const MIGRATION_4 = `
ALTER TABLE entry_mirror ADD COLUMN link_id TEXT;
ALTER TABLE entry_mirror ADD COLUMN source_entry_id TEXT;
`;

/**
 * Migration 5: the search index.
 *
 * Two tables. `entry_search` holds the WORDS of an entry, tokenized; nothing
 * else in this file is a virtual table, and Drizzle cannot model one, so it is
 * created here and read only through raw SQL. `search_doc` holds the columns
 * FTS5 cannot be addressed by, and is a Drizzle table like every other.
 *
 * Both are created EMPTY. A migration cannot fill them: a document is rendered
 * from an entry's values AND its record's schema by JavaScript, which no
 * statement here can call. The backfill runs after launch, per record, and
 * stamps `search_ver:<recordId>` in sync_meta when a record is done.
 *
 * Three decisions are baked into the DDL and each one was measured:
 *
 *   tokenize = 'unicode61 remove_diacritics 2'
 *     Not trigram. Trigram is more precise on Devanagari names, and it needs
 *     three characters: a two-letter search returns NOTHING under it, against
 *     50 rows in 3.5ms under unicode61 at fifty thousand documents. A wrong
 *     extra row is a nuisance; an empty result reads as a broken feature.
 *
 *   NO prefix index
 *     `prefix='2 3'` cost about 45% of the index size and bought almost
 *     nothing: FTS5 serves a prefix query from the term index without it, at
 *     3.5ms over fifty thousand documents. Final size is about 9MB there.
 *
 *   three columns and not one
 *     bm25 takes a weight per column, which is the whole of the ranking:
 *     a name outranks a note word, and a hit reached through a reference sits
 *     between the two.
 */
const MIGRATION_5 = `
CREATE VIRTUAL TABLE entry_search USING fts5(
  title,
  body,
  refs,
  tokenize = 'unicode61 remove_diacritics 2'
);

CREATE TABLE search_doc (
  doc_id INTEGER PRIMARY KEY,
  entry_id TEXT NOT NULL UNIQUE,
  record_id TEXT NOT NULL,
  day TEXT
);

CREATE INDEX search_doc_record ON search_doc(record_id);
`;

/**
 * Migration 6: which business each row belongs to.
 *
 * The device used to hold exactly one business, because the app picked
 * `memberships[0]` and never offered another. With a switcher, one phone holds
 * several — and six of the seven tables could not say which row was whose. The
 * two that made it urgent: an outbox drained under the wrong tenant id has its
 * ops REFUSED and dropped, which loses somebody's unsent sales; and a pull for
 * one business, seeing the other's records in the catalog, would prune them.
 *
 * **Three columns and not seven.** `entry_mirror`, `outbox` and
 * `record_catalog` are the tables something scans WITHOUT naming a record.
 * `schema_cache`, `insight_layout` and `search_doc` are keyed BY record id,
 * and a record id is a uuid — unique across businesses — so a keyed lookup
 * cannot bleed. Where one of those tables must be scanned business-wide, the
 * scan goes through the catalog instead:
 * `record_id IN (SELECT id FROM record_catalog WHERE tenant_id = ?)`. The
 * catalog is a dozen rows; the subquery is free. `sync_meta` keys already
 * carry the tenant where it matters, and `business_insight_layout` is keyed by
 * it outright.
 *
 * **The backfill reads the tenant out of the cursor key.** A pre-switcher
 * device is single-tenant by construction and its one tenant id is already in
 * the database, as the key `since:<tenantId>`. `substr` is 1-based and
 * 'since:' is six characters, so `substr(key, 7)` is the id. A device that
 * never synced has no such key and empty tables, and the guarded UPDATE then
 * does nothing, which is right.
 *
 * **The count guard is the important half.** A scalar subquery over two rows
 * silently answers with the first one, so on a device holding two cursors the
 * unguarded UPDATE would file half the rows under the wrong business — and a
 * row labelled with the wrong tenant is worse than a row labelled with none: a
 * null matches no tenant predicate, so it is hidden from everybody until a
 * pull re-files it, while a wrong label shows one shop's takings to another.
 * The pre-switcher app cannot write two cursors, so this guard should never
 * fire; it exists because the cost of being wrong is not symmetric.
 *
 * `tenant_id` stays nullable, which is all SQLite's ALTER TABLE allows and all
 * that is wanted: after the backfill every write path sets it, and a NULL
 * matches no predicate — fail closed.
 *
 * **`entry_mirror_day` is deliberately LEFT IN PLACE.** The business-wide
 * cards want an index that leads on the tenant, and they get one here. But
 * their statements only carry a tenant once `BusinessContext` is given one,
 * and the screens that build it are the next packet's work; dropping the
 * day-only index today would put every business card on a full mirror scan in
 * the meantime. The next migration should drop `entry_mirror_day` once no
 * statement can be compiled without a tenant. (Migration 7 does.)
 */
const MIGRATION_6 = `
ALTER TABLE entry_mirror ADD COLUMN tenant_id TEXT;
ALTER TABLE outbox ADD COLUMN tenant_id TEXT;
ALTER TABLE record_catalog ADD COLUMN tenant_id TEXT;

UPDATE entry_mirror
   SET tenant_id = (SELECT substr(key, 7) FROM sync_meta WHERE key LIKE 'since:%')
 WHERE (SELECT count(*) FROM sync_meta WHERE key LIKE 'since:%') = 1;

UPDATE outbox
   SET tenant_id = (SELECT substr(key, 7) FROM sync_meta WHERE key LIKE 'since:%')
 WHERE (SELECT count(*) FROM sync_meta WHERE key LIKE 'since:%') = 1;

UPDATE record_catalog
   SET tenant_id = (SELECT substr(key, 7) FROM sync_meta WHERE key LIKE 'since:%')
 WHERE (SELECT count(*) FROM sync_meta WHERE key LIKE 'since:%') = 1;

CREATE INDEX entry_mirror_tenant_day ON entry_mirror(tenant_id, day) WHERE deleted_at IS NULL;
CREATE INDEX record_catalog_tenant ON record_catalog(tenant_id);
CREATE INDEX outbox_tenant ON outbox(tenant_id, created_at);
`;

/**
 * Migration 7: the day-only index goes, now that nothing can use it.
 *
 * `entry_mirror_day` was migration 3's answer to a business-wide question that
 * names no record. Migration 6 gave the same questions a better index —
 * `entry_mirror_tenant_day`, leading on the business — but kept the old one,
 * because the compiler could still be handed a context with no tenant in it
 * and a statement that carries no tenant cannot use an index that leads on
 * one.
 *
 * `BusinessContext.tenantId` is now required and non-nullable, and the only
 * screen that builds one answers nothing until the active business is known.
 * So no statement in this app can be compiled that would use a day-only index,
 * and every index that no query can use is pure cost: a second b-tree written
 * on every entry inserted, updated, deleted or pulled — on the hottest write
 * path there is — plus its share of the database file.
 *
 * Nothing else reads it. `entry_mirror_list` and `entry_mirror_agg` lead on
 * `record_id` and serve every per-record question; `entry_mirror_seq` serves
 * the cursor. `insight-business-query.sqlite.test.ts` reads the real query
 * plans and fails if a business-wide card ever falls back to a scan.
 */
const MIGRATION_7 = `
DROP INDEX entry_mirror_day;
`;

/**
 * Migration 8: what the caller may DO in each record.
 *
 * `RecordListItem.grant` now travels on every pull and every record list, so
 * the catalog stores it. `grant_capabilities` is a JSON array.
 *
 * Null everywhere after this runs, and the reader in `record-grant.ts` treats
 * null as PERMISSIVE — null is what an owner sends, and it is also what a
 * device that has not synced since this shipped is left holding. Failing closed
 * would lock a person out of their own records after an upgrade, offline, on a
 * layer that is a courtesy and never a fence.
 *
 * **This migration deletes NOTHING, and an earlier draft of it did.** Until
 * 1 Sep 2026 the sync pull ignored `row_scope` and `view_all_entries`, so a
 * person cut down to their own entries has other people's rows in
 * `entry_mirror` and a server fix cannot reach back and take them away. Wiping
 * the mirror here would have cleared them — and charged a full re-pull to every
 * device on the platform, including the owners, who are most of them and who
 * leak nothing.
 *
 * `pruneUnreadableRows` in `entry-repo.ts` does it precisely instead, on the
 * first pull that carries a grant. It costs an owner nothing, it removes only
 * the rows the server would no longer send, and unlike a migration it runs
 * again every pull — so it also covers the case a migration cannot reach: an
 * owner narrowing somebody's grant NEXT month.
 */
const MIGRATION_8 = `
ALTER TABLE record_catalog ADD COLUMN grant_capabilities TEXT;
ALTER TABLE record_catalog ADD COLUMN grant_row_scope TEXT;
ALTER TABLE record_catalog ADD COLUMN grant_write_mode TEXT;
ALTER TABLE record_catalog ADD COLUMN grant_balance_visible INTEGER;
ALTER TABLE record_catalog ADD COLUMN grant_direction TEXT;
`;

/**
 * Migration 9: which fields the caller may WRITE in each record.
 *
 * `RecordGrantSummary.writableFields` joined the pull once the server started
 * enforcing `writable_fields` (docs/plans/done/grant-fences.md, Phase 1). A form
 * that offers a field the save will refuse is, offline, a 4xx and a dropped
 * outbox row, so the new-entry form leaves those fields undrawn — and it needs
 * the list on the catalog row to do it. A JSON array, like `grant_capabilities`.
 *
 * Null everywhere after this runs, and null means every field: an owner, a
 * grant that does not narrow, or a device that has not synced since this
 * shipped. The same permissive reading as migration 8, for the same reason.
 */
const MIGRATION_9 = `
ALTER TABLE record_catalog ADD COLUMN grant_writable_fields TEXT;
`;

/**
 * Migration 10: what each record HOLDS, as the glyph draws it.
 *
 * `RecordListItem.icon` joined the record list and the pull with
 * docs/plans/done/record-icons-and-list-tiles.md, and the home screen draws its tile
 * from it. Without the column every list on the device wears the grey list
 * glyph until the app is reinstalled, because the catalog is the only thing
 * home reads.
 *
 * One nullable TEXT column, which is all SQLite's ALTER TABLE allows and all
 * that is wanted. Null after this runs, and null is read exactly as "no icon":
 * a directory then falls back to the list glyph and a record to its direction
 * glyph, which is what both drew yesterday. The first pull fills it, and the
 * server answers the key on every row.
 *
 * No backfill is possible and none is needed. The effective icon is worked out
 * from a record's live fields on the SERVER — a directory with a phone field is
 * `people` — and no statement here can see a field set.
 */
const MIGRATION_10 = `
ALTER TABLE record_catalog ADD COLUMN icon TEXT;
`;

/**
 * Migration 11. One index that answers two questions about a record from the
 * index alone: how many live rows it holds, and whether a second person wrote
 * any of them.
 *
 * The record screen asks both as it draws — "how many" for its count line and
 * its empty state, "a second writer?" for whether each row names its author —
 * and on a record of two lakh rows each read the table itself, a fifth of a
 * second to a whole second at a time, several times as the screen settled.
 * `is_current` second, so the count is a prefix; `created_by` third, so the
 * next writer after the last is one seek. Partial on `deleted_at IS NULL`,
 * like every other mirror index, because a deleted row wrote nothing anybody
 * reads.
 */
const MIGRATION_11 = `
CREATE INDEX entry_mirror_record_live ON entry_mirror(record_id, is_current, created_by) WHERE deleted_at IS NULL;
`;

/**
 * Migration 12: the day buckets, and the floor each record has been pruned to.
 *
 * `aggregate_bucket` is one row per record, per measure, per day — the
 * `record_day` scope of Section 5.10, mirrored. It is what a card reads once
 * the rows themselves are gone: three years of a busy record is about eleven
 * hundred bucket rows and half a million entries, so the buckets stay while the
 * entries go.
 *
 * FOUR numbers on one row, not one number on four rows. The net, the two sides
 * of it, and the count. A net cannot be taken apart again once it is summed — a
 * day with a 500 in and a 500 out nets to nought, and "money in" is not nought
 * — so both sides are carried. The count is there because a record with no
 * measure can still be counted, and a count is the one number a pruned mirror
 * cannot work out from the rows it no longer holds.
 *
 * `record_catalog.floor_day` is the oldest day that record still holds, or NULL
 * for a record that has never been pruned and so holds everything. It is per
 * record and not per business, because the cap is per record: a record that
 * writes four hundred rows a day reaches its cap in a week while a quiet one
 * never reaches it at all, and one floor for both would either strand the quiet
 * record or lie about the busy one.
 *
 * `updated_at` is deliberately absent. A bucket carries `as_of_seq`, which is
 * what says whether it has moved, and a second timestamp would be a second
 * answer to the same question.
 */
const MIGRATION_12 = `
CREATE TABLE aggregate_bucket (
  tenant_id TEXT NOT NULL,
  record_id TEXT NOT NULL,
  day TEXT NOT NULL,
  measure_field_id TEXT,
  value REAL NOT NULL DEFAULT 0,
  rows INTEGER NOT NULL DEFAULT 0,
  money_in REAL NOT NULL DEFAULT 0,
  money_out REAL NOT NULL DEFAULT 0,
  as_of_seq INTEGER NOT NULL,
  PRIMARY KEY (tenant_id, record_id, day)
);
CREATE INDEX aggregate_bucket_span ON aggregate_bucket(record_id, day);
CREATE INDEX aggregate_bucket_tenant ON aggregate_bucket(tenant_id, day);
ALTER TABLE record_catalog ADD COLUMN floor_day TEXT;
`;

/**
 * Migration 13: whose rows the person reads in each record, and the mask the
 * rows were cut to (web-redesign-04 phase 3).
 *
 * `grant_reach` is the summary's `reach` as JSON: Off, Own, Their team or
 * Everyone for each entry permission. `grant_team` is the authors Their team
 * covers, a JSON array, only when See entries is Their team. `grant_mask` is
 * the digest of the fields hidden from them in the record.
 *
 * Null after this runs, and null is unknown: `pruneUnreadableRows` then falls
 * back to `grant_row_scope`, which never prunes a row the person may read, and
 * no re-read is asked for. The first pull fills all three.
 */
const MIGRATION_13 = `
ALTER TABLE record_catalog ADD COLUMN grant_reach TEXT;
ALTER TABLE record_catalog ADD COLUMN grant_team TEXT;
ALTER TABLE record_catalog ADD COLUMN grant_mask TEXT;
`;

/**
 * Migration 14: the lines a queued create or edit carries.
 *
 * An entry with lines is one write (Decision 5 of
 * docs/plans/entry-lines-and-layout.md): the entry and every line under it,
 * at both levels, leave in ONE outbox op and land in one server transaction.
 * The lines are a tree keyed by `lines` field id, which `values_json` cannot
 * hold beside the values without every reader of it learning a second shape,
 * so they get a column of their own.
 *
 * Nullable, and null is "this op names no lines": every op queued before this
 * build, and every write of a record with no lines field. An edit that names a
 * `lines` field sends its full set; one that does not leaves the stored lines
 * where they are, which is exactly what null says.
 */
const MIGRATION_14 = `
ALTER TABLE outbox ADD COLUMN lines_json TEXT;
`;

/**
 * Migration 15: the lines a queued edit wrote over, as they stood before it.
 *
 * An edit writes its lines into the mirror at once (`updatePending`), over
 * the rows the server holds: a line it changes takes the new values, a line
 * it leaves out is tombstoned, a line it adds is inserted. When the server
 * refuses the edit (`dropEditOp`) none of that is true, and no pull will say
 * so: the server holds what it held, and the cursor is already past it. So
 * the op keeps each line it wrote over, and a refusal puts them back.
 *
 * Nullable, and null is "nothing to put back": a create (a refused one
 * takes its lines away whole), an edit of a record with no lines, and every
 * op queued before this build.
 */
const MIGRATION_15 = `
ALTER TABLE outbox ADD COLUMN lines_before_json TEXT;
`;

/**
 * Every migration, in order. Index 0 takes a database from version 0 to 1.
 *
 * A ladder rather than a chain of ifs, for one reason: the old form ran only
 * the step matching the CURRENT version and then stamped the newest number,
 * so a fresh install would have taken migration 1, skipped migration 2, and
 * still claimed to be at version 2. Deriving DATABASE_VERSION from the array
 * means the count and the list cannot disagree.
 */
export const MIGRATIONS: readonly string[] = [
  MIGRATION_1,
  MIGRATION_2,
  MIGRATION_3,
  MIGRATION_4,
  MIGRATION_5,
  MIGRATION_6,
  MIGRATION_7,
  MIGRATION_8,
  MIGRATION_9,
  MIGRATION_10,
  MIGRATION_11,
  MIGRATION_12,
  MIGRATION_13,
  MIGRATION_14,
  MIGRATION_15,
];

export const DATABASE_VERSION = MIGRATIONS.length;
